/** Whether your own stream can be watched without a login, and the switch.
 *
 * The spectate page tells a turned-away visitor that "a driver turns
 * spectating on from their profile", and the profile is where the switch
 * lived — three screens from the page a driver is looking at when they wonder
 * why a friend sees "private". So the same switch is offered where the
 * question arises: on the Spectate index when you are signed in, and on your
 * own stream's page. It is the same one column and one `PATCH /api/me` the
 * profile uses. The reply is the account row, and the state shown is read
 * from that reply rather than from a session refresh, because refreshing the
 * session re-renders the shell around a page that is holding a WebSocket
 * open, and the stream must not blink because its visibility changed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ErrorNote } from '@/components/ui';
import { updateAccount } from '@/lib/api';
import { useMe } from '@/lib/session';

export interface LiveVisibility {
  userId: string;
  isPublic: boolean;
  saving: boolean;
  error: unknown;
  setPublic: (next: boolean) => Promise<void>;
}

/** Null for a visitor who is not signed in, who has no stream to switch. */
export function useLiveVisibility(): LiveVisibility | null {
  const { me } = useMe();
  const [chosen, setChosen] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setPublic = useCallback(async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const user = await updateAccount({ live_public: next });
      if (alive.current) setChosen(user.live_public);
    } catch (thrown) {
      if (alive.current) setError(thrown);
    } finally {
      if (alive.current) setSaving(false);
    }
  }, []);

  if (!me) return null;
  return {
    userId: me.user.id,
    isPublic: chosen ?? me.user.live_public,
    saving,
    error,
    setPublic,
  };
}

export interface StreamVisibilityProps {
  visibility: LiveVisibility;
  /** The stream's address, shown beside the state so the thing being made
   *  public is the thing on screen. */
  address: string;
  /** One line for a header; the full form adds what the choice means. */
  compact?: boolean;
}

export function StreamVisibility({ visibility, address, compact = false }: StreamVisibilityProps) {
  const { isPublic, saving, error, setPublic } = visibility;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontSize: compact ? 11.5 : 13,
        }}
      >
        <span
          style={{
            color: isPublic ? 'var(--color-accent-300)' : 'var(--color-neutral-500)',
            whiteSpace: 'nowrap',
          }}
        >
          your stream · {isPublic ? 'public' : 'private'}
        </span>
        {!compact ? (
          <span className="tabular-nums truncate" style={{ color: 'var(--color-neutral-600)' }}>
            {address}
          </span>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost"
          style={{ fontSize: 12, padding: '0 6px' }}
          disabled={saving}
          aria-busy={saving || undefined}
          onClick={() => void setPublic(!isPublic)}
        >
          {saving ? 'saving…' : isPublic ? 'make private' : 'make public'}
        </button>
      </div>

      {!compact ? (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-600)' }}>
          {isPublic
            ? 'Anyone with the address can watch while you are driving, no sign-in needed. ' +
              'Nothing is kept once a stream ends unless you opted into recording.'
            : 'Only you can watch. Make it public to share the address, or give overlays and ' +
              'race control a live:read token instead.'}
        </p>
      ) : null}

      {error ? <ErrorNote error={error} title="could not change your stream" compact /> : null}
    </div>
  );
}

export default StreamVisibility;
